import { ownerRef, publicRef, slugRef } from '@/backend/firebase/services';
import { getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import type {
  OwnerWithId,
  Public,
  Slug,
} from '../../../../../shared/src/schema';
import { db } from '../firebase';

export const SlugService = {
  slugify: (text: string) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD') // Separa acentos de letras
      .replace(/[\u0300-\u036f]/g, '') // Elimina los acentos
      .replace(/\s+/g, '-') // Espacios por guiones
      .replace(/[^\w-]+/g, '') // Elimina caracteres especiales
      .replace(/--+/g, '-'); // Evita guiones dobles
  },
  /**
   * Obtiene el documento del enlace por su ID (el string del slug)
   */
  getById: async (slug: string): Promise<Slug | null> => {
    const docRef = slugRef(slug);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      return snapshot.data() as Slug;
    }
    return null;
  },
  getMultiplePublicDataByIds: async (ownerIds: string[]): Promise<Public[]> => {
    try {
      const promises = ownerIds.map(async (id) => {
        const docRef = publicRef(id); // publicRef apunta a la colección "Public"
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          return snapshot.data() as Public;
        }
        return null;
      });

      const results = await Promise.all(promises);

      // Filtramos los nulos (por si pusiste un ID viejo o que no existe)
      return results.filter((r): r is Public => r !== null);
    } catch (error) {
      console.error(
        `[SlugService:getMultiplePublic] ❌ Error al buscar múltiples perfiles:`,
        error,
      );
      return [];
    }
  },
  /**
   * 1. VERIFICAR DISPONIBILIDAD
   * Retorna true si el enlace está libre, false si ya está ocupado.
   */
  isAvailable: async (slug: string, userId?: string): Promise<boolean> => {
    if (!slug || slug.length < 3) return false;

    // Limpiamos el slug de espacios o caracteres raros por seguridad
    const cleanSlug = slug.toLowerCase().trim().replace(/\s+/g, '-');
    const docRef = slugRef(cleanSlug);
    const snapshot = await getDoc(docRef);

    // 1. Si NO existe, está 100% disponible
    if (!snapshot.exists()) return true;

    // 2. Si existe, verificamos si el dueño actual es el mismo usuario que está consultando
    const slugData = snapshot.data();
    if (userId && slugData?.ownerId === userId) {
      return true; // Es de él, así que está "disponible" para que lo siga usando
    }

    // 3. Si existe y NO es de él, entonces está ocupado
    return false;
  },

  /**
   * 2. CREAR ENLACE
   * Vincula un slug con un propietario en la colección global de redirecciones.
   */
  // services/slug-service.ts

  create: async (slug: string, profile: OwnerWithId): Promise<void> => {
    const cleanSlug = SlugService.slugify(slug);
    if (cleanSlug.length < 3) throw new Error('Mínimo 3 caracteres.');

    const batch = writeBatch(db); // Importa 'db' y 'writeBatch' de firestore

    // A. Registro de la ruta global
    const slugDocRef = slugRef(cleanSlug);
    batch.set(slugDocRef, {
      id: cleanSlug,
      ownerId: profile.id,
      entityType: profile.account_type || 'professional',
      active: true,
      slug: cleanSlug,
      audit: { createdAt: serverTimestamp(), createdBy: profile.id },
    });

    // B. Actualización perfil PRIVADO (Usando notación de puntos para seguridad)
    const ownerDocRef = ownerRef(profile.id);
    batch.update(ownerDocRef, {
      'public.branding.slug': cleanSlug,
      'audit.updatedAt': serverTimestamp(),
    });

    // C. Actualización perfil PÚBLICO
    const pubDocRef = publicRef(profile.id);
    batch.set(
      pubDocRef,
      {
        branding: { slug: cleanSlug },
      },
      { merge: true },
    );

    try {
      // Se ejecutan todas las operaciones en una sola petición a Firebase
      await batch.commit();
    } catch (error) {
      console.error('Error en batch de slug:', error);
      throw new Error('No se pudo vincular el enlace. Intenta de nuevo.');
    }
  },
  /**
   * Obtiene los datos públicos del negocio a través de su enlace
   */
  getPublicData: async (slug: string): Promise<Public | null> => {
    try {
      // 1. Buscamos el documento en la colección global de slugs
      const slugDoc = await SlugService.getById(slug);

      if (!slugDoc) {
        console.warn(
          `[SlugService:getPublicData] ⚠️ El slug "${slug}" no existe o fue eliminado.`,
        );
        return null;
      }

      const ownerId = slugDoc.ownerId;

      // 2. Buscamos la configuración pública vinculada a ese ownerId
      const docRef = publicRef(ownerId);
      const snapshot = await getDoc(docRef);

      if (!snapshot.exists()) {
        console.warn(
          `[SlugService:getPublicData] ⚠️ El owner "${ownerId}" no tiene un documento de configuración (Public) creado.`,
        );
        return null;
      }

      const data = snapshot.data() as Public;

      return data;
    } catch (error) {
      console.error(
        `[SlugService:getPublicData] ❌ Error crítico al consultar Firebase para "${slug}":`,
        error,
      );
      // Importante: Lanzamos el error para que useQuery (React Query) sepa que falló la petición
      throw error;
    }
  },
};
